import { useQuery } from '@tanstack/react-query'

import { UseVacanciesQueryOptions, UseVacanciesQueryResult } from './types'
import { useService } from '@/hooks/useService'
import {
  CareersService,
  GetJobsResponse,
} from '../../../services/CareersService'
import { useEffect, useState } from 'react'
import {
  addJobCountToDepartments,
  filterJobsByDepartments,
  filterJobsByLocations,
  mapJobsToArticles,
} from './helpers'

/**
 * Shared hook that fetches raw jobs data without transformation
 * This ensures all components get the same base data
 */
export const useRawJobs = () => {
  const careers = useService(new CareersService('/careers'))

  return useQuery({
    queryKey: ['jobs', 'raw'],
    queryFn: async () => {
      const { data } = await careers.getJobs<GetJobsResponse>()
      return data
    },
    staleTime: 60 * 60 * 1000,
  })
}

/**
 * useJobs is a custom hook that fetches vacancies data
 * from the Greenhouse API and returns it in a paginated manner.
 *
 * @param {object} parameters - The parameters for the vacancies query.
 * @param {number} parameters.targetPageSize - The target number of items per page. Defaults to 6.
 * @param {number[]} parameters.filterDepartments - The department IDs to filter vacancies by.
 * @param {string[]} parameters.filterLocations - The location names to filter vacancies by.
 * @param {object} options - Additional options for the useQuery hook.
 *
 * @returns {object} The result of the useInfiniteQuery hook.
 */
export const useJobs = ({
  targetPageSize = 6,
  filterDepartments,
  filterLocations = [],
  articles,
  ...queryOptions
}: UseVacanciesQueryOptions) => {
  const [endCursor, setEndCursor] = useState(targetPageSize)

  // Use the shared raw jobs hook
  const rawJobsResult = useRawJobs()

  useEffect(() => {
    if (
      (filterDepartments && filterDepartments.length > 0) ||
      (filterLocations && filterLocations.length > 0)
    ) {
      setEndCursor(targetPageSize)
    }
  }, [filterDepartments, filterLocations])

  // Transform raw data locally
  const jobs = mapJobsToArticles(rawJobsResult.data?.jobs ?? [], articles ?? [])
  const departments = addJobCountToDepartments(
    jobs,
    rawJobsResult.data?.departments ?? [],
  )

  const filteredByDepartments = filterJobsByDepartments(jobs, filterDepartments)

  const filteredJobs = filterJobsByLocations(
    filteredByDepartments,
    filterLocations,
  )

  const paginatedJobs = filteredJobs.slice(0, endCursor) ?? []

  const locations = new Set<string>()
  filteredByDepartments.forEach((job) => {
    if (job.location?.name) {
      locations.add(job.location.name)
    }
  })

  return {
    ...rawJobsResult,
    data: {
      jobs: paginatedJobs,
      departments,
      total: filteredJobs.length,
      locations: Array.from(locations),
      allJobs: filteredByDepartments,
    },
    loadMore: () => setEndCursor(endCursor + targetPageSize),
    hasNextPage: paginatedJobs.length < filteredJobs.length,
  }
}
